import { useEffect, useState } from "react";
import { BookMarked } from "lucide-react";

/**
 * Font-family picker for the Legal Practice source panel. Options mirror the
 * typography an Indian high court uses on real judgement letterhead:
 *   - "mono"       → clean modern reference (default, matches typed text)
 *   - "playfair"   → Playfair Display · Supreme Court letterhead vibe
 *   - "cormorant"  → Cormorant Garamond · classic reported-judgement typography
 * Persists the choice per-student to localStorage.
 */
export const DEFAULT_LEGAL_FONT = "mono";
const STORAGE_KEY = "stenosync.legalFont.v1";

export function loadLegalFont() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v || DEFAULT_LEGAL_FONT;
  } catch { return DEFAULT_LEGAL_FONT; }
}

export function legalFontClass(v) {
  if (v === "playfair") return "font-serif-playfair";
  if (v === "cormorant") return "font-serif-cormorant";
  return "font-mono";
}

const OPTIONS = [
  { id: "mono",      label: "Mono",     desc: "Modern reference" },
  { id: "playfair",  label: "Playfair", desc: "Supreme Court letterhead" },
  { id: "cormorant", label: "Cormorant", desc: "Classic judgement" },
];

export default function LegalFontToggle({ value, onChange, testId = "legal-font-toggle" }) {
  const [local, setLocal] = useState(value || DEFAULT_LEGAL_FONT);
  useEffect(() => { setLocal(value || DEFAULT_LEGAL_FONT); }, [value]);

  const pick = (id) => {
    setLocal(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
    onChange?.(id);
  };

  return (
    <div
      className="inline-flex items-center gap-1 border border-border rounded-lg bg-secondary/40 pl-3 pr-1 py-1"
      data-testid={testId}
    >
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mr-1">
        <BookMarked className="w-3.5 h-3.5" /> Serif mode
      </span>
      {OPTIONS.map((o) => {
        const on = local === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => pick(o.id)}
            data-testid={`${testId}-${o.id}`}
            aria-pressed={on}
            title={o.desc}
            className={`h-7 px-3 rounded-md text-[10px] uppercase tracking-widest font-bold transition-colors ${legalFontClass(o.id)} ${
              on
                ? "bg-brand-gold text-background"
                : "text-muted-foreground hover:text-foreground hover:bg-background/60"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
